/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import classNames from 'classnames';
import { transparentize, lighten } from 'polished';

import {
   completeTutorial,
   completeTutorialStep,
   submitQuizAnswer,
} from './actions';
import { Button } from '../components/Button';
import { exerciseStages } from '../shared/constants';
import { tutorialCssConstants } from '../util/cssConstants';
import { addOrientationChangeListenerInEffect } from '../util/addOrientationChangeListenerInEffect';

const TutorialWrapper = styled.div`
   display: flex;
   justify-content: center;
   font-family: var(--content-font);

   ${(props) =>
      props.tutorialHeight != null
         ? `--tutorial-height: ${props.tutorialHeight}px;`
         : ''}

   & > div {
      background-color: var(--brand-white);
      box-shadow: 0 5px 10px ${transparentize(0.9, '#006061')};
      padding: var(--tutorial-padding) var(--tutorial-padding-right)
         var(--tutorial-padding-bottom) var(--tutorial-padding-left);
   }

   @media (prefers-color-scheme: dark) {
      & > div {
         background-color: rgba(0, 0, 0, 0.6);

         box-shadow: 0 5px 10px rgba(0, 0, 0, 0.4);
      }
   }

   p {
      line-height: 1.6;
   }

   strong {
      -webkit-text-stroke: 0.025em;
      letter-spacing: 0.0275em;
      font-weight: bold;
   }

   em {
      font-style: italic;
   }

   p:nth-last-child(n + 2) {
      margin-bottom: 1.5em;
   }

   .tutorial-action-prompt {
      font-weight: bold;
      text-align: center;
   }

   .tutorial-quiz-feedback {
      margin-top: -0.5em;
      font-weight: bold;
      text-align: center;
   }

   .tutorial-correct {
      color: var(--semantic-success-interact);
   }

   .tutorial-incorrect {
      color: var(--semantic-error);
   }

   @media (prefers-color-scheme: dark) {
      .tutorial-correct {
         color: var(--semantic-success);
      }

      .tutorial-incorrect {
         color: ${lighten(0.15, '#c51b29')};
      }
   }

   .tutorial-quiz-score {
      font-size: 1.25em;

      strong {
         font-weight: bold;
      }
   }

   .tutorial-button-wrapper {
      text-align: center;

      button {
         min-width: 8em;
         padding-left: 2em;
         padding-right: 2em;
      }
   }

   .mobile-portrait-tutorial {
      height: var(--tutorial-height);
      width: 100vw;

      .tutorial-text-wrapper-wrapper-lol {
         min-height: 3em;
         display: flex;
         justify-content: center;
         align-items: center;
         max-width: ${tutorialCssConstants.desktopMaxWidth}px;
         margin-left: auto;
         margin-right: auto;
      }

      .tutorial-text-wrapper {
         max-height: calc(
            var(--tutorial-height) - var(--tutorial-padding) -
               var(--tutorial-padding-bottom) - 44px - 1em
         );
         overflow: auto;
         overscroll-behavior: contain;
      }

      .tutorial-button-wrapper {
         margin-top: 1em;
      }

      position: fixed;
      bottom: 0;
      left: 0;
   }

   .mobile-landscape-tutorial {
      height: var(--tutorial-height);
      width: 100vw;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--tutorial-padding);
      padding-right: max(
         var(--tutorial-padding-right),
         calc((100vw - 800px) / 2)
      );
      padding-bottom: var(--tutorial-padding-bottom);
      padding-left: max(
         var(--tutorial-padding-left),
         calc((100vw - 800px) / 2)
      );

      .tutorial-text-wrapper {
         max-height: calc(
            var(--tutorial-height) - var(--tutorial-padding) -
               var(--tutorial-padding-bottom)
         );
         overflow: auto;
         overscroll-behavior: contain;
      }

      .tutorial-button-wrapper {
         margin-left: 1em;
         flex-shrink: 0;

         button {
            padding-left: 1em;
            padding-right: 1em;
         }
      }

      position: fixed;
      bottom: 0;
      left: 0;
   }

   .full-tutorial {
      padding: 1.5em;
      border-radius: 10px;
      min-width: ${tutorialCssConstants.desktopMinWidth}px;
      max-width: ${tutorialCssConstants.desktopMaxWidth}px;

      .tutorial-text-wrapper {
         max-height: calc(var(--tutorial-height) - 44px - 4em);
         overflow: auto;
         overscroll-behavior: contain;
      }

      .tutorial-button-wrapper {
         margin-top: 1em;
      }
   }

   .modal-tutorial {
      margin-top: 1.5em;
      padding: 1.5em;
      border-radius: 10px;
      min-width: ${tutorialCssConstants.desktopMinWidth}px;
      max-width: ${tutorialCssConstants.desktopMaxWidth}px;

      .tutorial-button-wrapper {
         margin-top: 1em;
      }
   }
`;

export const StepText = ({ text }) => {
   const styledText = useMemo(
      () =>
         (Array.isArray(text) ? text : [text]).map((paragraph) => {
            if (paragraph == null) {
               return '';
            }

            const reg = /(\*\*|~~|\*~|~\*)/;

            const arr = paragraph.split(reg);

            const newArr = [];

            let i = 0;

            while (i < arr.length) {
               const val = arr[i];
               switch (val) {
                  case '**': {
                     newArr.push(<strong key={i + 1}>{arr[i + 1]}</strong>);
                     i += 3;
                     break;
                  }
                  case '~~': {
                     newArr.push(<em key={i + 1}>{arr[i + 1]}</em>);
                     i += 3;
                     break;
                  }
                  case '*~': {
                     newArr.push(
                        <strong key={i + 1}>
                           <em>{arr[i + 1]}</em>
                        </strong>
                     );
                     i += 3;
                     break;
                  }
                  case '': {
                     i += 1;
                     break;
                  }
                  default: {
                     newArr.push(<span key={i}>{val}</span>);
                     i += 1;
                  }
               }
            }

            return newArr;
         }),
      [text]
   );

   return styledText.map((currentText, i) => <p key={i}>{currentText}</p>);
};

const mapNextButtonStateToProps = () => ({});

const mapNextButtonDispatchToProps = {
   completeTutorialStep,
   completeTutorial,
   submitQuizAnswer,
};
const NextButton = connect(
   mapNextButtonStateToProps,
   mapNextButtonDispatchToProps
)(
   ({
      allowSkip,
      quizAnswerSubmitted,
      currentStep,
      completeTutorialStep,
      completeTutorial,
      submitQuizAnswer,
      onCompleteTutorial,
   }) => {
      const showQuizButton = currentStep.isQuestion && !quizAnswerSubmitted;
      let nextButtonText;
      if (
         (currentStep.requiredActions || currentStep.requiredInterfaceState) &&
         !allowSkip
      ) {
         return null;
      }
      if (allowSkip) {
         nextButtonText = 'Skip';
      } else if (showQuizButton) {
         nextButtonText = currentStep.submitButtonText || 'Submit';
      } else {
         nextButtonText = currentStep.nextButtonText || 'Next';
      }

      let clickHandler = currentStep.completeTutorialOnNext
         ? onCompleteTutorial ?? completeTutorial
         : completeTutorialStep;
      if (showQuizButton) {
         clickHandler = submitQuizAnswer;
      }

      return (
         <Button className="button-cta" type="button" onClick={clickHandler}>
            {nextButtonText}
         </Button>
      );
   }
);

const mapStateToProps = ({ tutorial }) => ({
   tutorial,
});

const useTutorialStyles = (hasTutorial, isViewScore) => {
   const [tutorialStyles, setTutorialStyles] = useState(null);

   useEffect(() => {
      if (!isViewScore && hasTutorial) {
         const updateTutorialStyles = () => {
            const rect = document
               .querySelector('[data-interface-wrapper]')
               ?.getBoundingClientRect();

            const tutorialZoneWidth = window.innerWidth;
            const tutorialZoneHeight = Math.floor(
               window.innerHeight - rect.bottom
            );
            let tutorialClass = 'mobile-landscape-tutorial';

            if (
               tutorialZoneHeight >
                  tutorialCssConstants.initialHeightPortrait - 5 &&
               tutorialZoneWidth >= tutorialCssConstants.desktopMaxWidth
            ) {
               tutorialClass = 'full-tutorial';
            } else if (
               tutorialZoneHeight >
               tutorialCssConstants.initialHeightPortrait - 5
            ) {
               tutorialClass = 'mobile-portrait-tutorial';
            }

            setTutorialStyles({
               tutorialClass,
               tutorialHeight: tutorialZoneHeight,
            });
         };

         // Perform this after render so tutorial has a chance to initiallize the UI first
         setTimeout(updateTutorialStyles);

         return addOrientationChangeListenerInEffect(updateTutorialStyles);
      }
      if (hasTutorial) {
         setTutorialStyles({
            tutorialClass: 'modal-tutorial',
         });
      }
      return undefined;
   }, [hasTutorial]);

   return tutorialStyles;
};

const Tutorial = connect(mapStateToProps)(
   ({ tutorial, viewScore, hideForStage, onComplete }) => {
      const currentSectionIndex = tutorial?.section;
      const currentStepIndex = tutorial?.step;

      const [cachedTutorialStep, setCachedTutorialStep] = useState({
         section: currentSectionIndex,
         step: currentStepIndex,
      });

      const isViewScore =
         viewScore &&
         tutorial &&
         tutorial.tutorialDocument?.sections?.[currentSectionIndex].steps?.[
            currentStepIndex
         ]?.exerciseStage === exerciseStages.viewScore;

      useEffect(() => {
         if (isViewScore) {
            setCachedTutorialStep({
               section: currentSectionIndex,
               step: currentStepIndex,
            });
         }
      }, [viewScore, isViewScore, currentSectionIndex, currentStepIndex]);

      const hasTutorial = tutorial?.tutorialDocument != null;
      const tutorialStyles = useTutorialStyles(hasTutorial, isViewScore);

      if (!hasTutorial) {
         return null;
      }

      if (tutorialStyles == null) {
         return null;
      }

      const sectionIndex = viewScore
         ? cachedTutorialStep.section
         : tutorial?.section;

      const stepIndex = viewScore ? cachedTutorialStep.step : tutorial?.step;

      const currentSection =
         tutorial.tutorialDocument?.sections?.[sectionIndex];
      const currentStep = currentSection?.steps?.[stepIndex];

      if (hideForStage && currentStep.exerciseStage === hideForStage) {
         return null;
      }

      if (currentStep?.showQuizResults) {
         const results = tutorial.quizResponses.reduce(
            ({ totalCorrect, total }, currentIsCorrect) => ({
               totalCorrect: totalCorrect + (currentIsCorrect ? 1 : 0),
               total: total + 1,
            }),
            { totalCorrect: 0, total: 0 }
         );
         return (
            <TutorialWrapper tutorialHeight={tutorialStyles.tutorialHeight}>
               <div className={tutorialStyles.tutorialClass}>
                  <div className="tutorial-text-wrapper-wrapper-lol">
                     <div className="tutorial-text-wrapper">
                        <p className="tutorial-quiz-score">
                           Quiz Score:{' '}
                           <strong>
                              {(results.totalCorrect / results.total) * 100}%
                           </strong>
                        </p>
                     </div>
                  </div>
                  <div className="tutorial-button-wrapper">
                     <NextButton
                        currentStep={currentStep}
                        allowSkip={tutorial.allowSkip}
                        onCompleteTutorial={onComplete}
                     />
                  </div>
               </div>
            </TutorialWrapper>
         );
      }
      if (currentStep?.isQuestion && tutorial.quizAnswerSubmitted) {
         return (
            <TutorialWrapper tutorialHeight={tutorialStyles.tutorialHeight}>
               <div className={tutorialStyles.tutorialClass}>
                  <div className="tutorial-text-wrapper-wrapper-lol">
                     <div className="tutorial-text-wrapper">
                        <StepText text={currentStep?.text} />
                        <p
                           className={classNames({
                              'tutorial-quiz-feedback': true,
                              'tutorial-correct': tutorial.quizAnswerCorrect,
                              'tutorial-incorrect': !tutorial.quizAnswerCorrect,
                           })}
                        >
                           {' '}
                           Your answer is{' '}
                           {tutorial.quizAnswerCorrect
                              ? 'correct!'
                              : 'incorrect.'}{' '}
                        </p>
                     </div>
                  </div>
                  <div className="tutorial-button-wrapper">
                     <NextButton
                        quizAnswerSubmitted={tutorial.quizAnswerSubmitted}
                        currentStep={currentStep}
                        allowSkip={tutorial.allowSkip}
                     />
                  </div>
               </div>
            </TutorialWrapper>
         );
      }

      return (
         <TutorialWrapper tutorialHeight={tutorialStyles.tutorialHeight}>
            <div className={tutorialStyles.tutorialClass}>
               <div className="tutorial-text-wrapper-wrapper-lol">
                  <div className="tutorial-text-wrapper">
                     <StepText text={currentStep?.text} />
                  </div>
               </div>
               <div className="tutorial-button-wrapper">
                  {currentStep?.actionDescription && !tutorial.allowSkip && (
                     <p className="tutorial-action-prompt">{`(${currentStep?.actionDescription})`}</p>
                  )}
                  {((!currentStep.requiredActions &&
                     !currentStep.requiredInterfaceState) ||
                     tutorial.allowSkip) && (
                     <NextButton
                        currentStep={currentStep}
                        allowSkip={tutorial.allowSkip}
                        quizAnswerSubmitted={tutorial.quizAnswerSubmitted}
                        onCompleteTutorial={onComplete}
                     />
                  )}
               </div>
            </div>
         </TutorialWrapper>
      );
   }
);

const ChallengePrompt = ({ challengePrompt, children }) => {
   const tutorialStyles = useTutorialStyles(true, false);

   if (tutorialStyles == null) {
      return null;
   }

   return (
      <TutorialWrapper tutorialHeight={tutorialStyles.tutorialHeight}>
         <div className={tutorialStyles.tutorialClass}>
            <div className="tutorial-text-wrapper-wrapper-lol">
               <div className="tutorial-text-wrapper">
                  <StepText text={challengePrompt} />
               </div>
            </div>
            <div className="tutorial-button-wrapper">{children}</div>
         </div>
      </TutorialWrapper>
   );
};

export { Tutorial, TutorialWrapper, ChallengePrompt };
